import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import { LinkPage, handleLeaveLink } from '../components/ButtonExitLink';
import { errorPageStyles } from '../components/ComponentStyles';
import { useAppState } from '../contexts/AppStateProvider';
// Use new hooks instead of legacy context
import { useJWT } from '../contexts/JWTProvider';
import { ServiceThemeOptions } from '../themes';
import {
  getAnalyticsContext,
  getJWTErrorCaption,
  getJWTErrorMessage,
  getNavigationAction,
  getServiceDisplayName,
} from '../utils/jwtHelpers';

export default function ErrorPage() {
  const theme = useTheme() as ServiceThemeOptions;
  const navigate = useNavigate();
  const { state, search } = useLocation();

  // Use centralized JWT management and app state
  const jwt = useJWT();
  const { serviceName } = useAppState();

  // Get navigation actions - use custom actions from state if provided, otherwise use JWT-based actions
  const defaultNavigationAction = getNavigationAction(jwt);
  const navigationAction = state?.navigationAction || defaultNavigationAction;

  const serviceDisplayName = getServiceDisplayName(
    jwt,
    state?.serviceName || serviceName
  );

  // Determine redirect URI (prefer from JWT, fallback to context)
  const redirectURI = jwt.preferredRedirectURI || state?.redirectURIError;

  // Extract theme values safely
  const warningColor = (theme.palette?.warning as any)?.main || '#FD9726';
  const paperBgColor = theme.palette?.background?.paper || '#FFFFFF';

  // Use JWT-aware error messages if no state is provided, passing service name
  const errorMessage = state?.errorMsg || getJWTErrorMessage(jwt);
  const errorCaption =
    state?.errorCaption ||
    getJWTErrorCaption(jwt, state?.serviceName || serviceName);

  // Track page view with enhanced JWT context
  useEffect(() => {
    const analyticsContext = getAnalyticsContext(
      jwt,
      state?.serviceName || serviceName
    );

    amplitude.track('Error Page View', {
      ...analyticsContext,
      errorMessage: errorMessage,
      errorCaption: errorCaption,
      errorType: state?.errorType || jwt.errorType,
    });
  }, [state?.serviceName, serviceName, jwt, state, errorMessage, errorCaption]);

  // Handle primary action based on navigation action
  const handlePrimaryAction = () => {
    const analyticsContext = getAnalyticsContext(
      jwt,
      state?.serviceName || serviceName
    );

    amplitude.track('User Clicked Primary Action', {
      ...analyticsContext,
      action: navigationAction.primaryAction,
      errorMessage: errorMessage,
      errorCaption: errorCaption,
    });

    switch (navigationAction.primaryAction) {
      case 'try_again':
        navigate({
          pathname: '/selectTSP',
          search: search,
        });
        break;
      case 'return_to_service':
        if (redirectURI) {
          handleLeaveLink({
            token: jwt.rawToken || '',
            redirectURIError: redirectURI,
            serviceName:
              state?.serviceName || serviceName || serviceDisplayName,
            pageOnExit: LinkPage.Approve,
          });
        }
        break;
    }
  };

  // Handle secondary action based on navigation action
  const handleSecondaryAction = () => {
    const analyticsContext = getAnalyticsContext(
      jwt,
      state?.serviceName || serviceName
    );

    amplitude.track('User Clicked Secondary Action', {
      ...analyticsContext,
      action: navigationAction.secondaryAction,
      redirectURL: redirectURI,
    });

    switch (navigationAction.secondaryAction) {
      case 'return_to_service':
        if (redirectURI) {
          handleLeaveLink({
            token: jwt.rawToken || '',
            redirectURIError: redirectURI,
            serviceName:
              state?.serviceName || serviceName || serviceDisplayName,
            pageOnExit: LinkPage.Approve,
          });
        }
        break;
      case 'select_provider':
        navigate({
          pathname: '/selectTSP',
          search: search,
        });
        break;
    }
  };

  return (
    <Box sx={errorPageStyles.mainContainer}>
      <Paper
        elevation={3}
        sx={{
          ...errorPageStyles.paper,
          bgcolor: paperBgColor,
        }}
      >
        {/* Icon Container */}
        <Box sx={errorPageStyles.iconContainer}>
          <ErrorOutlineIcon
            sx={{
              fontSize: 94,
              color: warningColor,
            }}
          />
        </Box>

        {/* Error Message */}
        <Typography
          variant='h4'
          sx={{
            ...errorPageStyles.title(theme as any),
          }}
        >
          {errorMessage}
        </Typography>

        {/* Error Caption */}
        <Typography
          variant='body1'
          sx={{
            ...errorPageStyles.caption(theme as any),
          }}
        >
          {errorCaption}
        </Typography>

        {/* Buttons */}
        <Stack spacing={1.25} sx={errorPageStyles.buttonsContainer}>
          {/* Primary Action */}
          {navigationAction.primaryAction === 'return_to_service' &&
          !redirectURI ? (
            <Typography
              variant='body2'
              sx={{
                textAlign: 'center',
                fontStyle: 'italic',
                color: 'text.secondary',
                mt: 2,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              You can safely close this tab or window.
            </Typography>
          ) : (
            <Button
              variant='contained'
              onClick={handlePrimaryAction}
              sx={errorPageStyles.primaryButton(theme as any)}
            >
              {navigationAction.primaryLabel || 'GO TO START'}
            </Button>
          )}

          {/* Secondary Action */}
          {navigationAction.secondaryAction && (
            <Button
              variant='text'
              onClick={handleSecondaryAction}
              sx={errorPageStyles.secondaryButton(theme as any)}
            >
              {navigationAction.secondaryLabel}
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
